/* eslint-disable */
import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBackIos,
  ArrowForwardIos,
  CalendarMonth,
  ViewList,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  LinkOff as LinkOffIcon,
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { useQuery, useMutation } from "@apollo/client";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { GET_SCHEDULED_POSTS } from "../../gql/queries/scrapy";
import { CANCEL_SCHEDULED_POST, RESCHEDULE_POST } from "../../gql/mutations/scrapy";
import { usePopup } from "../../context/commonPop";
import SchedulePostDialog from "./SchedulePostDialog";
import RescheduleDialog from "./RescheduleDialog";

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: "calc(100vh - 70px)",
  backgroundColor: theme.palette.background.default,
  padding: "24px",
}));

const CalendarGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(7, 1fr)",
  gap: "1px",
  backgroundColor: theme.palette.divider,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "8px",
  overflow: "hidden",
}));

const CalendarCell = styled(Box)(({ theme, isCurrentMonth, isToday }) => ({
  minHeight: "110px",
  padding: "8px",
  backgroundColor: isToday
    ? theme.palette.primary.light + "20"
    : isCurrentMonth
    ? theme.palette.background.paper
    : theme.palette.action.hover,
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  cursor: "default",
}));

const DayLabel = styled(Typography)(({ theme, isToday }) => ({
  fontWeight: isToday ? 700 : 400,
  color: isToday ? theme.palette.primary.main : theme.palette.text.secondary,
  fontSize: "0.82rem",
  lineHeight: 1,
  marginBottom: "4px",
}));

const STATUS_COLORS = {
  scheduled: "primary",
  published: "success",
  failed: "error",
  cancelled: "default",
};

const STATUS_LABELS = {
  scheduled: "Scheduled",
  published: "Published",
  failed: "Failed",
  cancelled: "Cancelled",
};

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const isProjectConnected = (project) =>
  project?.isConnected === true &&
  project?.integrationToken &&
  Object.keys(project.integrationToken).length > 0;

export default function ContentCalendar() {
  const theme = useTheme();
  const customizer = useSelector((s) => s.customizer);
  const { handleOpen } = usePopup();

  const [tabValue, setTabValue] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(dayjs().startOf("month"));
  const [openSchedule, setOpenSchedule] = useState(false);
  const [reschedulePost, setReschedulePost] = useState(null);

  const { data, loading, refetch } = useQuery(GET_SCHEDULED_POSTS, {
    fetchPolicy: "cache-first",
  });
  const posts = data?.scheduledPosts || [];

  const [cancelPost] = useMutation(CANCEL_SCHEDULED_POST);

  const handleCancel = async (postId) => {
    try {
      await cancelPost({ variables: { postId } });
      handleOpen(true, "Post cancelled successfully");
      refetch();
    } catch (e) {
      handleOpen(false, e.message || "Failed to cancel post");
    }
  };

  // Build calendar days for current month view
  const buildCalendarDays = useCallback(() => {
    const startOfMonth = currentMonth.startOf("month");
    const endOfMonth = currentMonth.endOf("month");
    const startDay = startOfMonth.day(); // 0=Sun
    const days = [];

    // Fill leading empty cells from prev month
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({ date: startOfMonth.subtract(i + 1, "day"), isCurrentMonth: false });
    }
    // Fill current month
    for (let d = 0; d < endOfMonth.date(); d++) {
      days.push({ date: startOfMonth.add(d, "day"), isCurrentMonth: true });
    }
    // Fill trailing cells to complete 6 rows
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: endOfMonth.add(i, "day"), isCurrentMonth: false });
    }
    return days;
  }, [currentMonth]);

  const getPostsForDay = (date) => {
    return posts.filter((p) => dayjs(p.publishAt).format("YYYY-MM-DD") === date.format("YYYY-MM-DD"));
  };

  // DataGrid columns for list view
  const columns = [
    {
      field: "title",
      headerName: "Blog Title",
      flex: 1,
      minWidth: 200,
      renderCell: (p) => (
        <Tooltip title={p.value}>
          <Typography noWrap variant="body2">{p.value}</Typography>
        </Tooltip>
      ),
    },
    {
      field: "project",
      headerName: "Website",
      width: 220,
      renderCell: (p) => {
        const connected = isProjectConnected(p.row.project);
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title={connected ? "Connected" : "Not connected"} arrow>
              {connected ? <LinkIcon sx={{ fontSize: 16, color: "success.main" }} /> : <LinkOffIcon sx={{ fontSize: 16, color: "warning.main" }} />}
            </Tooltip>
            <Typography variant="body2" noWrap>
              {p.row.project?.projectName || p.row.project?.website || p.row.projectId}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "platform",
      headerName: "Platform",
      width: 110,
      renderCell: (p) => (
        <Chip label={p.row.project?.appType || "—"} size="small" variant="outlined" />
      ),
    },
    {
      field: "publishAt",
      headerName: "Scheduled At",
      width: 180,
      renderCell: (p) => (
        <Typography variant="body2">
          {dayjs(p.value).format("MMM D, YYYY h:mm A")}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (p) => (
        <Chip
          label={STATUS_LABELS[p.value] || p.value}
          color={STATUS_COLORS[p.value] || "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (p) =>
        p.row.status === "scheduled" ? (
          <Box display="flex" gap={0.5}>
            <Tooltip title="Reschedule">
              <IconButton size="small" onClick={() => setReschedulePost(p.row)}>
                <ScheduleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancel">
              <IconButton size="small" color="error" onClick={() => handleCancel(p.row.id)}>
                <CancelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : p.row.status === "published" && p.row.publishedPostUrl ? (
          <Tooltip title="View published post">
            <IconButton size="small" color="success" component="a" href={p.row.publishedPostUrl} target="_blank" rel="noopener noreferrer">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null,
    },
  ];

  const calendarDays = buildCalendarDays();

  return (
    <PageWrapper>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Content Calendar
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Schedule and manage your blog posts across all connected platforms
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenSchedule(true)}
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}
          >
            Schedule Post
          </Button>
        </Box>
      </Box>

      {/* Stats row */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: "Total Scheduled", value: posts.filter((p) => p.status === "scheduled").length, color: theme.palette.primary.main },
          { label: "Published", value: posts.filter((p) => p.status === "published").length, color: theme.palette.success.main },
          { label: "Failed", value: posts.filter((p) => p.status === "failed").length, color: theme.palette.error.main },
          { label: "Cancelled", value: posts.filter((p) => p.status === "cancelled").length, color: theme.palette.text.disabled },
        ].map((stat) => (
          <Grid item xs={6} sm={3} key={stat.label}>
            <Paper elevation={0} sx={{ p: 2, borderRadius: "10px", border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h4" fontWeight={700} color={stat.color}>
                {stat.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stat.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab icon={<CalendarMonth fontSize="small" />} iconPosition="start" label="Calendar" />
          <Tab icon={<ViewList fontSize="small" />} iconPosition="start" label="List" />
        </Tabs>
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {/* Calendar View */}
      {!loading && tabValue === 0 && (
        <Box>
          {/* Month navigation */}
          <Box display="flex" alignItems="center" justifyContent="center" gap={2} mb={2}>
            <IconButton onClick={() => setCurrentMonth((m) => m.subtract(1, "month"))}>
              <ArrowBackIos fontSize="small" />
            </IconButton>
            <Typography variant="h6" fontWeight={600} minWidth={160} textAlign="center">
              {currentMonth.format("MMMM YYYY")}
            </Typography>
            <IconButton onClick={() => setCurrentMonth((m) => m.add(1, "month"))}>
              <ArrowForwardIos fontSize="small" />
            </IconButton>
          </Box>

          {/* Day headers */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(7, 1fr)"
            mb="1px"
            gap="1px"
            bgcolor={theme.palette.divider}
            border={`1px solid ${theme.palette.divider}`}
            borderBottom="none"
            borderRadius="8px 8px 0 0"
            overflow="hidden"
          >
            {DAYS_OF_WEEK.map((d) => (
              <Box
                key={d}
                bgcolor={theme.palette.background.paper}
                py={1}
                textAlign="center"
              >
                <Typography variant="caption" fontWeight={600} color="text.secondary">
                  {d}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Calendar grid */}
          <CalendarGrid>
            {calendarDays.map(({ date, isCurrentMonth }, idx) => {
              const isToday = date.isSame(dayjs(), "day");
              const dayPosts = getPostsForDay(date);
              return (
                <CalendarCell key={idx} isCurrentMonth={isCurrentMonth} isToday={isToday}>
                  <DayLabel isToday={isToday}>{date.date()}</DayLabel>
                  {dayPosts.slice(0, 3).map((post) => {
                    const connected = isProjectConnected(post.project);
                    return (
                      <Tooltip
                        key={post.id}
                        title={
                          `${post.title} — ${post.project?.projectName || post.project?.website || ""}` +
                          (!connected ? " (Not connected)" : "")
                        }
                      >
                        <Chip
                          icon={!connected ? <LinkOffIcon sx={{ fontSize: 14 }} /> : undefined}
                          label={post.title.length > 18 ? post.title.slice(0, 18) + "…" : post.title}
                          color={STATUS_COLORS[post.status]}
                          size="small"
                          sx={{ fontSize: "0.68rem", height: "20px", cursor: "pointer" }}
                          onClick={() => post.status === "scheduled" && setReschedulePost(post)}
                          onDelete={post.status === "scheduled" ? () => handleCancel(post.id) : undefined}
                        />
                      </Tooltip>
                    );
                  })}
                  {dayPosts.length > 3 && (
                    <Typography variant="caption" color="text.secondary">
                      +{dayPosts.length - 3} more
                    </Typography>
                  )}
                </CalendarCell>
              );
            })}
          </CalendarGrid>
        </Box>
      )}

      {/* List View */}
      {!loading && tabValue === 1 && (
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: "10px", overflow: "hidden" }}>
          <DataGrid
            rows={posts.map((p) => ({ ...p, id: p.id || p._id }))}
            columns={columns}
            autoHeight
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            disableRowSelectionOnClick
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: theme.palette.background.default,
                fontWeight: 600,
              },
            }}
          />
        </Paper>
      )}

      {/* Dialogs */}
      <SchedulePostDialog
        open={openSchedule}
        onClose={() => setOpenSchedule(false)}
        onSuccess={() => { setOpenSchedule(false); refetch(); }}
      />
      {reschedulePost && (
        <RescheduleDialog
          post={reschedulePost}
          onClose={() => setReschedulePost(null)}
          onSuccess={() => { setReschedulePost(null); refetch(); }}
        />
      )}
    </PageWrapper>
  );
}
